import React, { useMemo } from "react";
import {
    SovosBox,
    SovosIconButton,
    SovosTypography,
} from "mosaic-react";
import { ContentCopy } from "mosaic-react-icons";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

const escapeIdentifier = (value) => String(value || "").replace(/`/g, "``");
const escapeLiteral = (value) => `'${String(value ?? "").replace(/'/g, "''")}'`;
const escapeLikePattern = (value) => String(value ?? "").replace(/[\\%_]/g, "\\$&");

const parseInValues = (value) => {
    try {
        const values = JSON.parse(value);
        return Array.isArray(values) ? values : [value];
    } catch {
        return String(value || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }
};

const buildWhereClause = ({ column_name: columnName, find_value: findValue, operator }) => {
    const column = `\`${escapeIdentifier(columnName)}\``;
    const clauses = {
        contains: `${column} LIKE ${escapeLiteral(`%${escapeLikePattern(findValue)}%`)} ESCAPE '\\\\'`,
        ends_with: `${column} LIKE ${escapeLiteral(`%${escapeLikePattern(findValue)}`)} ESCAPE '\\\\'`,
        eq: `${column} = ${escapeLiteral(findValue)}`,
        is_not_null: `${column} IS NOT NULL`,
        is_null: `${column} IS NULL`,
        neq: `${column} <> ${escapeLiteral(findValue)}`,
        starts_with: `${column} LIKE ${escapeLiteral(`${escapeLikePattern(findValue)}%`)} ESCAPE '\\\\'`,
    };

    if (operator === "in") {
        const values = parseInValues(findValue).map(escapeLiteral).join(", ");
        return `${column} IN (${values})`;
    }

    return clauses[operator] || `${column} = ${escapeLiteral(findValue)}`;
};

export const buildRuleSqlPreview = (rule) => {
    const column = `\`${escapeIdentifier(rule?.column_name)}\``;
    return [
        "UPDATE <staging_table>",
        `SET ${column} = ${escapeLiteral(rule?.replace_value)}`,
        `WHERE ${buildWhereClause(rule || {})};`,
    ].join("\n");
};

export const buildRuleSqlTemplate = (rule) => {
    const column = `\`${escapeIdentifier(rule?.column_name)}\``;
    const operators = {
        contains: `${column} LIKE ? ESCAPE '\\\\'`,
        ends_with: `${column} LIKE ? ESCAPE '\\\\'`,
        eq: `${column} = ?`,
        is_not_null: `${column} IS NOT NULL`,
        is_null: `${column} IS NULL`,
        neq: `${column} <> ?`,
        starts_with: `${column} LIKE ? ESCAPE '\\\\'`,
    };
    const where = rule?.operator === "in"
        ? `${column} IN (${parseInValues(rule?.find_value).map(() => "?").join(", ")})`
        : operators[rule?.operator] || `${column} = ?`;

    return [
        "UPDATE <staging_table>",
        `SET ${column} = ?`,
        `WHERE ${where};`,
    ].join("\n");
};

const RuleDetails = ({ description, rule }) => {
    const { i18n } = useGlobalValue();
    const sql = useMemo(() => buildRuleSqlPreview(rule), [rule]);
    const sqlTemplate = useMemo(() => buildRuleSqlTemplate(rule), [rule]);
    const handleCopy = () => {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(sqlTemplate).catch(() => null);
        }
    };

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
            <SovosTypography sx={{ color: "text.secondary" }} variant="body2">
                {description}
            </SovosTypography>
            <SovosBox
                sx={{
                    backgroundColor: "grey.100",
                    borderRadius: 1,
                    boxSizing: "border-box",
                    minHeight: "calc(100vh - 128px)",
                    p: 2,
                    position: "relative",
                }}
            >
                <SovosTypography
                    sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
                    variant="caption"
                >
                    {i18n`SQL`}
                </SovosTypography>
                <SovosIconButton
                    aria-label={i18n`Copy SQL template`}
                    onClick={handleCopy}
                    sx={{
                        color: "text.secondary",
                        position: "absolute",
                        right: 12,
                        top: 12,
                        "& svg": { fontSize: 16 },
                    }}
                    tooltipText={i18n`Copy SQL template`}
                >
                    <ContentCopy />
                </SovosIconButton>
                <SovosBox
                    component="pre"
                    sx={{
                        color: "text.primary",
                        fontFamily: "monospace",
                        fontSize: 13,
                        m: 0,
                        overflowX: "auto",
                        pr: 5,
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {sql}
                </SovosBox>
            </SovosBox>
        </SovosBox>
    );
};

export default RuleDetails;
